import { useState, useEffect } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import UrlForm from './components/UrlForm';
import UrlList from './components/UrlList';
import Header from './components/Header';
import AuthGuard from './components/AuthGuard';
import { urlService } from './services/urlService';
import { authService } from './services/authService';
import './App.css';

function App() {
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const fetchUrls = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const data = await urlService.getUrls();
      setUrls(data);
    } catch (error) {
      console.error('Error fetching URLs:', error);
      toast.error('Failed to fetch URLs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user) => {
      setUser(user);
      setAuthLoading(false);
      if (user) {
        fetchUrls();
      } else {
        setUrls([]);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      fetchUrls();
    }
  }, [user]);

  const handleUrlCreated = (newUrl) => {
    setUrls(prev => [newUrl, ...prev]);
  };

  const handleUrlUpdated = (updatedUrl) => {
    setUrls(prev => prev.map(url => 
      url.id === updatedUrl.id ? updatedUrl : url
    ));
  };

  const handleUrlDeleted = (deletedId) => {
    setUrls(prev => prev.filter(url => url.id !== deletedId));
  };

  const handleAuthChange = (newUser) => {
    setUser(newUser);
  };

  const handleSignIn = async () => {
    try {
      const user = await authService.signInWithGoogle();
      setUser(user);
      toast.success('Successfully signed in!');
    } catch (error) {
      toast.error(error.message || 'Failed to sign in');
    }
  };

  const filteredUrls = urls.filter(url =>
    url.originalUrl.toLowerCase().includes(searchTerm.toLowerCase()) ||
    url.shortId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Toaster position="top-right" />
      
      <Header user={user} onAuthChange={handleAuthChange} />
      
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <AuthGuard user={user} onSignIn={handleSignIn}>
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Create Short URL</h2>
            <UrlForm onUrlCreated={handleUrlCreated} />
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <h2 className="text-2xl font-bold text-gray-800">Your URLs ({urls.length})</h2>
              <div className="w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Search URLs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <UrlList
              urls={filteredUrls}
              loading={loading}
              onUrlUpdated={handleUrlUpdated}
              onUrlDeleted={handleUrlDeleted}
            />
          </div>
        </AuthGuard>
      </main>
    </div>
  );
}

export default App;
